import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FiX, FiCheck } from "react-icons/fi";
import { useSubscription } from "../../hooks/useSubscription";
import type { Plan } from "../../types/payments";

// Type-safe icon wrapper
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Override plan tier - use this when you have verified plan from URL */
  planTier?: Plan | null;
}

// Plan features by tier (only paid plans - this modal only shows after payment)
const planFeatures: Record<string, string[]> = {
  basic: ["Unlimited sends", "Unlimited automations", "Advanced analytics", "Priority email support"],
  pro: ["Everything in Basic", "Data room access", "Deck storage", "Advanced integrations"],
  enterprise: ["Everything in Pro", "Dedicated account manager", "Custom integrations", "SLA guarantees"],
};

const planNames: Record<string, string> = {
  basic: "Basic",
  pro: "Pro",
  enterprise: "Enterprise",
};

/**
 * PaymentSuccessModal - Shows subscription confirmation after successful payment
 *
 * Props:
 * - planTier: Verified plan from URL (preferred source of truth)
 * - Falls back to subscription hook if planTier not provided
 */
const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  planTier: propPlanTier,
}) => {
  const { features } = useSubscription();
  const [displayPlan, setDisplayPlan] = useState<string>("basic");

  useEffect(() => {
    // Priority: prop planTier > subscription hook planTier > default "basic"
    if (propPlanTier && ["basic", "pro", "enterprise"].includes(propPlanTier)) {
      setDisplayPlan(propPlanTier);
    } else if (features?.planTier && features.planTier !== "free") {
      setDisplayPlan(features.planTier);
    } else {
      // Default to basic for payment success (user just paid, shouldn't see "free")
      setDisplayPlan("basic");
    }
  }, [propPlanTier, features]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const currentPlanFeatures = planFeatures[displayPlan] || planFeatures.basic;
  const currentPlanName = planNames[displayPlan] || "Basic";

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal" style={{ maxWidth: "480px", marginTop: "10vh" }}>
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>SUBSCRIPTION CONFIRMED</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body" style={{ padding: "32px 24px", textAlign: "center" }}>
          {/* Success Icon */}
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "rgba(16, 185, 129, 0.2)",
              border: "2px solid #10B981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <CheckIcon style={{ width: "32px", height: "32px", color: "#10B981" }} />
          </div>

          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#FFFFFF",
              marginBottom: "8px",
            }}
          >
            You&apos;re Subscribed!
          </h2>

          <p style={{ color: "#9CA3AF", marginBottom: "24px", fontSize: "14px" }}>
            Your subscription to the <strong style={{ color: "#A855F7" }}>{currentPlanName}</strong> plan is now active.
          </p>

          {/* Features List */}
          <div
            style={{
              backgroundColor: "rgba(168, 85, 247, 0.1)",
              border: "1px solid rgba(168, 85, 247, 0.3)",
              padding: "16px",
              marginBottom: "24px",
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#A855F7",
                marginBottom: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Your Features
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {currentPlanFeatures.map((feature, idx) => (
                <li
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    color: "#D1D5DB",
                    marginBottom: idx < currentPlanFeatures.length - 1 ? "8px" : 0,
                  }}
                >
                  <CheckIcon style={{ color: "#10B981", width: "16px", height: "16px", flexShrink: 0 }} />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <p style={{ color: "#6B7280", fontSize: "12px" }}>
            Thank you for subscribing. Start exploring your new features!
          </p>
        </div>

        {/* Footer */}
        <div className="su-modal-footer" style={{ justifyContent: "center" }}>
          <button
            type="button"
            className="su-btn-primary"
            onClick={onClose}
            style={{ width: "100%", maxWidth: "200px" }}
          >
            GET STARTED
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentSuccessModal;
